import Component from "../../lib/Component.js";
import { randomUUID } from "../helpers.js";

/**
 * @typedef {{
 *  form: HTMLFormElement,
 *  on: (name: string, eventTypes: string, handler: (event: Event, element: HTMLElement) => void) => void,
 *  disable: () => void,
 *  enable: () => void,
 *  validateEmail: (control: HTMLInputElement) => string|null,
 *  validateField: (control: HTMLInputElement, check: (control: HTMLInputElement) => any) => any,
 *  setValue: (name: string, value: string) => void,
 *  setCheckbox: (name: string, checked: boolean) => void
 * } & Component} SettingsFormComponent
 */

/**
 * @typedef {{
 *  onSubmit: (event: Event, submitButton: HTMLButtonElement|null) => void
 * }} SettingsFormOptions
 */

/**
 * @typedef {{
 *  name: string,
 *  eventType: string,
 *  eventHandler: (event: Event) => void
 * }} CustomEventHandler
 */

export class ValidationError extends Error {}

/**
 *
 * @param {HTMLFormElement} form
 * @param {SettingsFormOptions} options
 * @returns {SettingsFormComponent}
 */
const SettingsForm = (form, options = {}) => {
  const opts = {
    ...options,
  };

  /** @type {HTMLElement|null} */
  let lastClickedSubmitButton = null;
  /** @type {HTMLElement[]} */
  const selectControls = form ? Array.from(form.querySelectorAll(".select-control")) : [];
  /** @type {HTMLElement[]} */
  const submitButtons = form ? Array.from(form.querySelectorAll(".submit-settings-form")) : [];

  /**
   * @param {Event} event
   */
  const selectControlChangeHandler = (event) => {
    const control = event.currentTarget.closest(".select-control");
    control.querySelector(".control-value").innerHTML = event.target.selectedOptions[0].text;
  };

  /**
   * @param {PointerEvent} event
   */
  const submitButtonClickHandler = (event) => {
    lastClickedSubmitButton = event.currentTarget;
  };

  /**
   * @param {SubmitEvent} event
   */
  const submitHandler = (event) => {
    const lastClickedSubmitButtonOnSubmit = lastClickedSubmitButton;
    lastClickedSubmitButton = null;
    component.disable();
    if (opts.onSubmit) opts.onSubmit(event, lastClickedSubmitButtonOnSubmit);
  };

  /** @type {CustomEventHandler[]} */
  const customEventHandlers = [];

  /** @type {SettingsFormComponent} */
  const component = form
    ? new Component({
        componentName: "SettingsForm",
        onMount: () => {
          selectControls.forEach((control) => {
            if (!control.id) control.id = `id-${randomUUID()}`;
            control.querySelector("select").addEventListener("change", selectControlChangeHandler, false);
          });
          submitButtons.forEach((button) => {
            button.addEventListener("click", submitButtonClickHandler, false);
          });
          form.addEventListener("submit", submitHandler, false);
        },
        onUnmount: () => {
          selectControls.forEach((control) => {
            control.querySelector("select").removeEventListener("change", selectControlChangeHandler, false);
          });
          submitButtons.forEach((button) => {
            button.removeEventListener("click", submitButtonClickHandler, false);
          });
          form.removeEventListener("submit", submitHandler, false);
          customEventHandlers.forEach(({ name, eventTypes, eventHandler }) => {
            eventTypes.split(" ").forEach((eventType) => {
              form[name].removeEventListener(eventType, eventHandler);
            });
          });
          component.enable();
        },
      })
    : Component.noop();

  /** @type {HTMLFormElement} */
  component.form = form;

  /** Register custom event handler for a form control. */
  component.on = (name, eventTypes, handler) => {
    if (!form) return;
    const eventHandler = (event) => handler(event, form[name]);
    customEventHandlers.push({ name, eventTypes, eventHandler });
    eventTypes.split(" ").forEach((eventType) => {
      form[name].addEventListener(eventType, eventHandler);
    });
  };

  /** Disable all submit buttons */
  component.disable = () => {
    if (!form) return;
    submitButtons.forEach((control) => {
      control.disabled = true;
    });
  };

  /** Enable all submit buttons */
  component.enable = () => {
    if (!form) return;
    submitButtons.forEach((control) => {
      control.disabled = false;
    });
  };

  component.validateField = (control, check) => {
    const controlLabel = control.closest("label");
    controlLabel.classList.remove("invalid", "valid");
    try {
      controlLabel.classList.add("valid");
      return check(control);
    } catch (e) {
      if (e instanceof ValidationError) {
        controlLabel.classList.add("invalid");
        controlLabel.querySelector(".feedback").innerHTML = e.message;
        return null;
      }
      throw e;
    }
  };

  component.validateEmail = (control) => {
    return component.validateField(control, (ctrl) => {
      if (!ctrl.value || ctrl.value.match(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/)) {
        return ctrl.value;
      }
      throw new ValidationError("Hibás email cím formátum.");
    });
  };

  component.setValue = (name, value) => {
    if (!component.form) return;
    component.form[name].value = value;
  };

  component.setCheckbox = (name, checked) => {
    if (!component.form) return;
    component.form[name].checked = checked;
  };

  return component;
};

export default SettingsForm;
